import { useEffect, useRef, useState } from 'react'

interface LightboxImage {
  src: string
  alt: string
}

interface DragState { x: number; y: number; tx: number; ty: number }
interface PinchState { d: number; scale: number; cx: number; cy: number; tx: number; ty: number }

const MIN_SCALE = 0.5
const MAX_SCALE = 6
const clampScale = (s: number) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s))

/**
 * 全局图片放大预览：灵感卡片 / 复习卡片 / 首页每日精读中的图片，
 * 点击后在黑色遮罩中全屏显示，支持双指缩放、单指拖动、滚轮缩放，
 * 再点一下图片（或背景 / Esc）回到卡片内部。
 * 通过事件委托捕获 .rich-content 内的 <img>，编辑器中不触发。
 */
export default function ImageLightbox() {
  const [image, setImage] = useState<LightboxImage | null>(null)
  const [scale, setScale] = useState(1)
  const [tx, setTx] = useState(0)
  const [ty, setTy] = useState(0)
  const imgRef = useRef<HTMLImageElement>(null)
  const boxRef = useRef<HTMLDivElement>(null)
  const pointersRef = useRef(new Map<number, { x: number; y: number }>())
  const gestureRef = useRef({ moved: false, zoomed: false })
  const dragRef = useRef<DragState | null>(null)
  const pinchRef = useRef<PinchState | null>(null)

  const close = () => {
    setImage(null)
    setScale(1)
    setTx(0)
    setTy(0)
  }

  // 全局点击委托：卡片内容里的图片 → 打开预览
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target
      if (!(target instanceof HTMLImageElement)) return
      if (!target.closest('.rich-content')) return
      // 编辑器内（contenteditable）点击图片是选中操作，不弹预览
      if (target.closest('[contenteditable]')) return
      if (target.closest('.image-lightbox')) return
      setImage({ src: target.currentSrc || target.src, alt: target.alt || '' })
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [])

  // Esc 关闭；打开时锁定页面滚动
  useEffect(() => {
    if (!image) return
    const original = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close() }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = original
      window.removeEventListener('keydown', onKey)
    }
  }, [image])

  // 滚轮缩放（原生监听以允许 preventDefault）；绑定整个遮罩容器，
  // 鼠标在图片或背景任意位置滚动都可缩放
  useEffect(() => {
    if (!image) return
    const el = boxRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15
      setScale(s => clampScale(s * factor))
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [image])

  const onPointerDown = (e: React.PointerEvent<HTMLImageElement>) => {
    e.currentTarget.setPointerCapture?.(e.pointerId)
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    gestureRef.current = { moved: false, zoomed: false }
    if (pointersRef.current.size === 1) {
      dragRef.current = { x: e.clientX, y: e.clientY, tx, ty }
      pinchRef.current = null
    } else if (pointersRef.current.size === 2) {
      const [a, b] = [...pointersRef.current.values()]
      pinchRef.current = {
        d: Math.hypot(a.x - b.x, a.y - b.y),
        scale,
        cx: (a.x + b.x) / 2,
        cy: (a.y + b.y) / 2,
        tx,
        ty,
      }
      dragRef.current = null
    }
  }

  const onPointerMove = (e: React.PointerEvent<HTMLImageElement>) => {
    if (!pointersRef.current.has(e.pointerId)) return
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    const pointers = [...pointersRef.current.values()]
    if (pointers.length === 1 && dragRef.current) {
      const dx = e.clientX - dragRef.current.x
      const dy = e.clientY - dragRef.current.y
      if (Math.abs(dx) + Math.abs(dy) > 4) gestureRef.current.moved = true
      setTx(dragRef.current.tx + dx)
      setTy(dragRef.current.ty + dy)
    } else if (pointers.length === 2 && pinchRef.current) {
      const [a, b] = pointers
      const d = Math.hypot(a.x - b.x, a.y - b.y)
      const next = clampScale(pinchRef.current.scale * (d / pinchRef.current.d))
      if (Math.abs(next - pinchRef.current.scale) > 0.02) gestureRef.current.zoomed = true
      // 以双指中心为缩放焦点，同步平移保持焦点不漂移
      const cx = (a.x + b.x) / 2
      const cy = (a.y + b.y) / 2
      setScale(next)
      setTx(pinchRef.current.tx + (cx - pinchRef.current.cx))
      setTy(pinchRef.current.ty + (cy - pinchRef.current.cy))
    }
  }

  const onPointerUp = (e: React.PointerEvent<HTMLImageElement>) => {
    const had = pointersRef.current.has(e.pointerId)
    pointersRef.current.delete(e.pointerId)
    // 纯点击（无拖动、无缩放）→ 关闭预览，回到卡片
    if (had && pointersRef.current.size === 0 && !gestureRef.current.moved && !gestureRef.current.zoomed) {
      close()
    }
  }

  if (!image) return null

  return (
    <div
      ref={boxRef}
      className="image-lightbox"
      onClick={e => { if (e.target === e.currentTarget) close() }}
    >
      <img
        ref={imgRef}
        src={image.src}
        alt={image.alt}
        draggable={false}
        style={{ transform: `translate(${tx}px, ${ty}px) scale(${scale})` }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      />
    </div>
  )
}
